import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { getRoleAccent } from "../utils/roleAccent";

// Largeurs de Paper communes à tous les drawers. Les drawers passent DRAWER_PAPER_PROPS
// (plus bas) et non ces objets bruts ; DRAWER_WIDTH reste exporté pour un futur drawer
// qui aurait besoin de composer son propre objet PaperProps.
export const DRAWER_WIDTH = {
  // Colonnes peu nombreuses.
  compact: { width: "min(90vw, 800px)" },
  // Tableaux de 6-7 colonnes avec des URLs complètes : ils seraient écrasés à 800px.
  wide: { width: "min(95vw, 1200px)" },
  // Grille qui s'enroule (flexWrap) et non tableau : un plafond fixe ferait retomber
  // les dernières colonnes à la ligne. Largeur dictée par le contenu, bornée au
  // viewport — Drawer.js ne pose aucune borne de son côté.
  fluid: { width: "auto", maxWidth: "95vw" },
};

// Objets PaperProps figés au niveau module : un littéral `{ sx: ... }` donnait une
// nouvelle identité de props à chaque render, donc une re-sérialisation Emotion du
// Paper pendant les frames de l'animation d'ouverture.
export const DRAWER_PAPER_PROPS = {
  compact: { sx: DRAWER_WIDTH.compact },
  wide: { sx: DRAWER_WIDTH.wide },
  fluid: { sx: DRAWER_WIDTH.fluid },
};

// Supprime le coulissement à l'ouverture, pour que tous les drawers arrivent de la même
// façon. MUI passe `appear: mounted.current` au Slide (Drawer.js), donc un drawer qui se
// démonte à la fermeture n'animait déjà pas son arrivée, alors qu'un drawer resté monté
// coulissait : le comportement dépendait du montage, ce qui se voyait. `timeout` surcharge
// bien celui du Slide, SlideProps étant spread après dans Drawer.js. Le fondu du backdrop
// est conservé : le régler passerait par transitionDuration, qui vise les deux à la fois.
export const DRAWER_NO_SLIDE = { timeout: 0 };

// Le titre ne doit jamais pousser la croix hors cadre : minWidth 0 autorise le
// flex item à se rétrécir sous la largeur de son contenu, overflowWrap coupe les
// noms de plugin sans espace.
const TEXT_BOX_SX = { flex: 1, minWidth: 0 };
const TITLE_SX = {
  fontSize: 24,
  fontWeight: 700,
  lineHeight: 1.25,
  color: "text.primary",
  overflowWrap: "anywhere",
};
const SUBTITLE_SX = { mt: 0.25 };
const ACTIONS_SX = { flexShrink: 0, alignSelf: "center" };
const CLOSE_SX = {
  flexShrink: 0,
  color: "text.secondary",
  "&:hover": { color: "text.primary" },
};

const DrawerHeader = ({ title, subtitle, actions, onClose, role }) => {
  const theme = useTheme();

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        px: 2.5,
        py: 1.75,
        // background.paper vaut rgba(255,255,255,0.95) en mode clair : un sticky
        // semi-transparent laisserait voir le contenu défiler derrière le titre.
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFFFFF",
        borderBottom: `4px solid ${getRoleAccent(role, theme)}`,
      }}
    >
      <Box sx={TEXT_BOX_SX}>
        <Typography component="h2" sx={TITLE_SX}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={SUBTITLE_SX}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {actions && <Box sx={ACTIONS_SX}>{actions}</Box>}

      <IconButton
        onClick={onClose}
        aria-label="Fermer le panneau"
        size="small"
        sx={CLOSE_SX}
      >
        <HighlightOffIcon />
      </IconButton>
    </Box>
  );
};

export default DrawerHeader;
